import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import {
  DocumentDuplicateIcon,
  CheckIcon,
  CodeBracketIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  EyeIcon,
  MinusIcon,
  PlusIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'

// ─── Deep clone ───────────────────────────────────────────────────────────────
const deepClone = (v) => JSON.parse(JSON.stringify(v))

// ─── Set value at path (immutable) ───────────────────────────────────────────
const setAtPath = (obj, path, value) => {
  if (path.length === 0) return value
  const [head, ...tail] = path
  if (Array.isArray(obj)) {
    const arr = [...obj]
    arr[Number(head)] = setAtPath(arr[Number(head)], tail, value)
    return arr
  }
  return { ...obj, [head]: setAtPath(obj[head] ?? null, tail, value) }
}

// ─── Editable primitive value ─────────────────────────────────────────────────
const EditableValue = ({ value, path, onUpdate }) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const inputRef = useRef(null)

  const startEdit = () => {
    setDraft(typeof value === 'string' ? value : JSON.stringify(value))
    setEditing(true)
  }

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus()
  }, [editing])

  const commit = () => {
    let parsed = draft
    if (typeof value === 'number') parsed = Number(draft)
    else if (typeof value === 'boolean') parsed = draft === 'true'
    onUpdate(path, parsed)
    setEditing(false)
  }

  const cancel = () => setEditing(false)

  if (typeof value === 'boolean') {
    return (
      <button
        onClick={() => onUpdate(path, !value)}
        className="text-xs px-1.5 py-0.5 rounded font-semibold font-mono bg-orange-50 text-orange-600 border border-orange-200 hover:bg-orange-100 transition-colors"
      >
        {String(value)}
      </button>
    )
  }

  if (value === null) {
    return <span className="text-gray-400 font-mono text-xs italic cursor-default">null</span>
  }

  if (editing) {
    const isLong = typeof value === 'string' && value.length > 60
    if (isLong) {
      return (
        <textarea
          ref={inputRef}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Escape') cancel() }}
          onBlur={commit}
          rows={Math.min(8, draft.split('\n').length + 1)}
          className="w-full text-xs font-mono border border-indigo-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-indigo-50 resize-y"
        />
      )
    }
    return (
      <input
        ref={inputRef}
        type={typeof value === 'number' ? 'number' : 'text'}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') cancel() }}
        onBlur={commit}
        className="text-xs font-mono border border-indigo-300 rounded px-2 py-0.5 focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-indigo-50 min-w-[120px] max-w-full"
      />
    )
  }

  const colorClass =
    typeof value === 'number' ? 'text-blue-600' :
    typeof value === 'string' ? 'text-emerald-700' : 'text-gray-600'

  const display = typeof value === 'string'
    ? (value.length > 100 ? `"${value.slice(0, 100)}…"` : `"${value}"`)
    : String(value)

  return (
    <span
      onClick={startEdit}
      title="Click para editar"
      className={`font-mono text-xs cursor-text hover:bg-yellow-50 hover:underline hover:decoration-dashed rounded px-0.5 transition-colors ${colorClass}`}
    >
      {display}
    </span>
  )
}

// ─── Collapse toggle button ───────────────────────────────────────────────────
const CollapseBtn = ({ open, onClick, count, type }) => (
  <button onClick={onClick} className="flex items-center gap-1 group focus:outline-none flex-shrink-0">
    <span className="flex items-center justify-center w-4 h-4 rounded text-gray-400 group-hover:text-indigo-500 group-hover:bg-indigo-50 transition-colors">
      {open ? <ChevronDownIcon className="w-3 h-3" /> : <ChevronRightIcon className="w-3 h-3" />}
    </span>
    {!open && (
      <span className="text-xs text-gray-400 font-mono">
        {type === 'array' ? `[ ${count} ]` : `{ ${count} }`}
      </span>
    )}
  </button>
)

// ─── Badge helper ─────────────────────────────────────────────────────────────
const getElementBadge = (key) => {
  if (!key) return null
  if (key === 'expectedFlow') return { label: 'validation', color: 'bg-purple-100 text-purple-700' }
  if (key === 'formFlow') return { label: 'formFlow', color: 'bg-green-100 text-green-700' }
  if (key === 'emailFields') return { label: 'emails', color: 'bg-blue-100 text-blue-700' }
  if (key.match(/initSMS$/)) return { label: 'SMS', color: 'bg-yellow-100 text-yellow-700' }
  if (key.match(/-init(-\d+\/\d+)?$/)) return { label: 'init', color: 'bg-indigo-100 text-indigo-700' }
  if (key.includes('Check') || key.includes('check')) return { label: 'check', color: 'bg-amber-100 text-amber-700' }
  if (key.includes('schedule') || key.includes('Schedule')) return { label: 'schedule', color: 'bg-cyan-100 text-cyan-700' }
  if (key.includes('thanks') || key.includes('Thanks') || key.includes('End')) return { label: 'end', color: 'bg-gray-100 text-gray-600' }
  return null
}

// ─── Generic recursive JSON node ─────────────────────────────────────────────
const JsonNode = ({ keyName, value, path, onUpdate, depth = 0, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen)

  const isObject = value !== null && typeof value === 'object' && !Array.isArray(value)
  const isArray = Array.isArray(value)
  const isPrimitive = !isObject && !isArray

  const entries = isObject ? Object.entries(value) : null
  const count = isObject ? entries.length : isArray ? value.length : 0
  const badge = getElementBadge(keyName)

  if (isPrimitive) {
    return (
      <div className="flex items-start gap-1.5 py-0.5 group">
        <span className="w-4 flex-shrink-0" />
        {keyName !== undefined && (
          <span className="font-mono text-xs text-violet-700 font-medium flex-shrink-0 whitespace-nowrap">{keyName}:</span>
        )}
        <EditableValue value={value} path={path} onUpdate={onUpdate} />
      </div>
    )
  }

  // Section card for top-level objects and arrays
  if (depth === 0 && keyName) {
    const sectionStyles = {
      expectedFlow: { dot: 'bg-purple-500', border: 'border-purple-200', header: 'bg-purple-50 hover:bg-purple-100', text: 'text-purple-800', badge: 'bg-white border-purple-200 text-purple-500' },
      formFlow:     { dot: 'bg-green-500',  border: 'border-green-200',  header: 'bg-green-50 hover:bg-green-100',   text: 'text-green-800',  badge: 'bg-white border-green-200 text-green-500' },
      emailFields:  { dot: 'bg-blue-500',   border: 'border-blue-200',   header: 'bg-blue-50 hover:bg-blue-100',     text: 'text-blue-800',   badge: 'bg-white border-blue-200 text-blue-500' },
    }
    const s = sectionStyles[keyName] || { dot: 'bg-gray-400', border: 'border-gray-200', header: 'bg-gray-50 hover:bg-gray-100', text: 'text-gray-700', badge: 'bg-white border-gray-200 text-gray-500' }
    return (
      <div className={`rounded-xl border ${s.border} overflow-hidden shadow-sm`}>
        <button
          onClick={() => setOpen(o => !o)}
          className={`w-full flex items-center gap-3 px-4 py-3 transition-colors text-left ${s.header}`}
        >
          <span className={`w-2 h-2 rounded-full ${s.dot} flex-shrink-0`} />
          <span className={`font-semibold text-sm flex-1 ${s.text}`}>{keyName}</span>
          <span className={`text-xs font-mono border px-2 py-0.5 rounded ${s.badge}`}>
            {isArray ? `${count} items` : `${count} keys`}
          </span>
          <span className={`flex items-center justify-center w-5 h-5 rounded transition-colors ${open ? 'bg-white/80 text-gray-600' : 'bg-white/50 text-gray-400'}`}>
            {open ? <ChevronDownIcon className="w-3 h-3" /> : <ChevronRightIcon className="w-3 h-3" />}
          </span>
        </button>
        {open && (
          <div className="p-3 space-y-1.5 border-t border-gray-100 bg-white">
            {isObject && entries.map(([k, v]) => (
              <JsonNode key={k} keyName={k} value={v} path={[...path, k]} onUpdate={onUpdate} depth={depth + 1} />
            ))}
            {isArray && value.map((v, i) => (
              <JsonNode key={i} keyName={String(i)} value={v} path={[...path, i]} onUpdate={onUpdate} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-start gap-1.5 py-0.5">
        <CollapseBtn open={open} onClick={() => setOpen(o => !o)} count={count} type={isArray ? 'array' : 'object'} />
        {keyName !== undefined && (
          <span className="font-mono text-xs text-violet-700 font-medium flex-shrink-0">{keyName}:</span>
        )}
        {badge && !open && (
          <span className={`text-xs px-1.5 py-0.5 rounded font-medium leading-none mt-0.5 ${badge.color}`}>{badge.label}</span>
        )}
      </div>
      {open && (
        <div className="ml-4 border-l-2 border-gray-100 pl-3 mt-0.5 space-y-0.5">
          {isObject && entries.map(([k, v]) => (
            <JsonNode key={k} keyName={k} value={v} path={[...path, k]} onUpdate={onUpdate} depth={depth + 1} />
          ))}
          {isArray && value.map((v, i) => (
            <JsonNode key={i} keyName={String(i)} value={v} path={[...path, i]} onUpdate={onUpdate} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Element card (per-element collapsible card in elements section) ──────────
const ElementCard = ({ keyName, value, path, onUpdate, forceOpen }) => {
  const [open, setOpen] = useState(forceOpen)
  useEffect(() => { setOpen(forceOpen) }, [forceOpen])

  if (typeof value !== 'object' || value === null) {
    return (
      <div className="flex items-start gap-2 px-3 py-2 rounded-lg border border-gray-100 bg-gray-50">
        <span className="font-mono text-xs font-semibold text-gray-700 flex-shrink-0">{keyName}:</span>
        <EditableValue value={value} path={path} onUpdate={onUpdate} />
      </div>
    )
  }

  const rcsMsg = value?.RCSMessage || {}
  const suggestions = rcsMsg.suggestedChipList?.suggestions || []
  const cards = rcsMsg.richcardMessage?.message?.generalPurposeCardCarousel?.content || []
  const hasText = !!rcsMsg.textMessage
  const hasSuggestions = suggestions.length > 0
  const hasCarousel = cards.length > 0

  const accentClass = hasCarousel ? 'border-pink-200' : hasSuggestions ? 'border-indigo-200' : 'border-gray-200'
  const headerClass = hasCarousel ? 'bg-pink-50 hover:bg-pink-100' : hasSuggestions ? 'bg-indigo-50 hover:bg-indigo-100' : 'bg-gray-50 hover:bg-gray-100'

  return (
    <div className={`rounded-lg border overflow-hidden ${accentClass}`}>
      <button
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center gap-2 px-3 py-2.5 transition-colors text-left ${headerClass}`}
      >
        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-white/80 flex-shrink-0">
          {open ? <ChevronDownIcon className="w-3 h-3 text-gray-500" /> : <ChevronRightIcon className="w-3 h-3 text-gray-400" />}
        </span>
        <span className="font-mono text-xs font-semibold text-gray-800 flex-1 truncate">{keyName}</span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {hasCarousel && <span className="text-xs bg-pink-100 text-pink-700 px-1.5 py-0.5 rounded font-medium">carousel · {cards.length}</span>}
          {hasSuggestions && <span className="text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded font-medium">{suggestions.length} chips</span>}
          {hasText && <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-medium">text</span>}
        </div>
      </button>

      {/* Preview collapsed */}
      {!open && (
        <div className="px-3 pb-2 pt-0.5 ml-7 space-y-1">
          {hasText && <p className="text-xs text-gray-400 truncate font-mono">{rcsMsg.textMessage?.slice(0, 120)}</p>}
          {hasCarousel && (
            <div className="flex gap-1 flex-wrap">
              {cards.slice(0, 5).map((c, i) => (
                <span key={i} className="text-xs bg-pink-50 text-pink-600 border border-pink-100 px-1.5 py-0.5 rounded truncate max-w-[140px]">{c.title}</span>
              ))}
              {cards.length > 5 && <span className="text-xs text-gray-400">+{cards.length - 5}</span>}
            </div>
          )}
          {hasSuggestions && !hasCarousel && (
            <div className="flex gap-1 flex-wrap">
              {suggestions.map((s, i) => {
                const txt = s.reply?.displayText || s.action?.displayText || '?'
                const isAction = !!s.action
                return (
                  <span key={i} className={`text-xs border px-1.5 py-0.5 rounded ${isAction ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-indigo-50 text-indigo-600 border-indigo-100'}`}>
                    {txt}
                  </span>
                )
              })}
            </div>
          )}
        </div>
      )}

      {open && (
        <div className="border-t border-gray-100 bg-white px-3 py-2 space-y-1">
          {Object.entries(value).map(([k, v]) => (
            <JsonNode key={k} keyName={k} value={v} path={[...path, k]} onUpdate={onUpdate} depth={1} defaultOpen={k === 'RCSMessage'} />
          ))}
        </div>
      )}
    </div>
  )
}

// ─── FormFlow section ─────────────────────────────────────────────────────────
const FormFlowSection = ({ formFlow, path, onUpdate }) => {
  const [open, setOpen] = useState(true)

  return (
    <div className="rounded-xl border border-green-200 overflow-hidden shadow-sm">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-green-50 hover:bg-green-100 transition-colors text-left"
      >
        <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
        <span className="font-semibold text-sm text-green-800 flex-1">formFlow</span>
        <span className="text-xs text-green-600 font-mono bg-white border border-green-200 px-2 py-0.5 rounded">
          {formFlow.length} pasos
        </span>
        <span className={`flex items-center justify-center w-5 h-5 rounded transition-colors ${open ? 'bg-green-200 text-green-700' : 'bg-green-100 text-green-500'}`}>
          {open ? <ChevronDownIcon className="w-3 h-3" /> : <ChevronRightIcon className="w-3 h-3" />}
        </span>
      </button>
      {open && (
        <div className="p-3 space-y-1.5 border-t border-green-100 bg-white">
          <p className="text-xs text-gray-400 px-1 pb-1">Secuencia de elementos que esperan respuesta libre del usuario:</p>
          {formFlow.map((key, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-green-100 bg-green-50/50">
              <span className="flex items-center justify-center w-5 h-5 rounded-full bg-green-200 text-green-800 text-xs font-bold flex-shrink-0">{i + 1}</span>
              <EditableValue value={key} path={[...path, i]} onUpdate={onUpdate} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── ExpectedFlow section ─────────────────────────────────────────────────────
const ExpectedFlowSection = ({ expectedFlow, path, onUpdate }) => {
  const [open, setOpen] = useState(true)
  const entries = Object.entries(expectedFlow)

  return (
    <div className="rounded-xl border border-purple-200 overflow-hidden shadow-sm">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-purple-50 hover:bg-purple-100 transition-colors text-left"
      >
        <span className="w-2 h-2 rounded-full bg-purple-500 flex-shrink-0" />
        <span className="font-semibold text-sm text-purple-800 flex-1">expectedFlow</span>
        <span className="text-xs text-purple-600 font-mono bg-white border border-purple-200 px-2 py-0.5 rounded">
          {entries.length} validaciones
        </span>
        <span className={`flex items-center justify-center w-5 h-5 rounded transition-colors ${open ? 'bg-purple-200 text-purple-700' : 'bg-purple-100 text-purple-500'}`}>
          {open ? <ChevronDownIcon className="w-3 h-3" /> : <ChevronRightIcon className="w-3 h-3" />}
        </span>
      </button>
      {open && (
        <div className="p-3 space-y-2.5 border-t border-purple-100 bg-white">
          {entries.map(([elementKey, rule]) => {
            const answers = rule.expectedAnswers || []
            const elementOk = rule.elementOk || ''
            const elementWrong = rule.elementWrong || ''
            const flowOk = rule.flowOk || []
            const flowWrong = rule.flowWrong || []
            const basePath = [...path, elementKey]
            return (
              <div key={elementKey} className="rounded-lg border border-purple-100 overflow-hidden">
                <div className="flex items-center gap-2 px-3 py-2 bg-purple-50">
                  <span className="w-1.5 h-1.5 rounded-full bg-purple-400 flex-shrink-0" />
                  <span className="font-mono text-xs font-semibold text-purple-800">{elementKey}</span>
                </div>
                <div className="px-3 py-2.5 space-y-2 bg-white">
                  {/* Espera */}
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide w-24 flex-shrink-0 pt-0.5">Espera:</span>
                    <div className="flex gap-1 flex-wrap flex-1">
                      {answers.map((ans, i) => (
                        <EditableValue key={i} value={ans} path={[...basePath, 'expectedAnswers', i]} onUpdate={onUpdate} />
                      ))}
                    </div>
                  </div>
                  {/* Ok */}
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-semibold text-green-600 uppercase tracking-wide w-24 flex-shrink-0 pt-0.5">✓ Ok:</span>
                    <div className="space-y-1 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-gray-400 w-16 flex-shrink-0">elemento</span>
                        <span className="text-gray-300">→</span>
                        <EditableValue value={elementOk} path={[...basePath, 'elementOk']} onUpdate={onUpdate} />
                      </div>
                      {flowOk.length > 0 && (
                        <div className="flex items-start gap-1.5">
                          <span className="text-xs text-gray-400 w-16 flex-shrink-0 pt-0.5">flujo</span>
                          <span className="text-gray-300 pt-0.5">→</span>
                          <div className="flex gap-1 flex-wrap">
                            {flowOk.map((f, i) => (
                              <EditableValue key={i} value={f} path={[...basePath, 'flowOk', i]} onUpdate={onUpdate} />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Error */}
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-semibold text-red-500 uppercase tracking-wide w-24 flex-shrink-0 pt-0.5">✗ Error:</span>
                    <div className="space-y-1 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-gray-400 w-16 flex-shrink-0">elemento</span>
                        <span className="text-gray-300">→</span>
                        <EditableValue value={elementWrong} path={[...basePath, 'elementWrong']} onUpdate={onUpdate} />
                      </div>
                      {flowWrong.length > 0 && (
                        <div className="flex items-start gap-1.5">
                          <span className="text-xs text-gray-400 w-16 flex-shrink-0 pt-0.5">flujo</span>
                          <span className="text-gray-300 pt-0.5">→</span>
                          <div className="flex gap-1 flex-wrap">
                            {flowWrong.map((f, i) => (
                              <EditableValue key={i} value={f} path={[...basePath, 'flowWrong', i]} onUpdate={onUpdate} />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── Elements section ─────────────────────────────────────────────────────────
const ElementsSection = ({ elements, path, onUpdate }) => {
  const [open, setOpen] = useState(true)
  const [allExpanded, setAllExpanded] = useState(false)

  const entries = Object.entries(elements)
  const elementEntries = entries.filter(([k]) => !['type', 'expectedFlow', 'formFlow', 'rbmFlowAliasesMap'].includes(k))
  const metaEntries = entries.filter(([k]) => k === 'type')

  return (
    <div className="rounded-xl border border-indigo-200 overflow-hidden shadow-sm">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-indigo-50 hover:bg-indigo-100 transition-colors text-left"
      >
        <span className="w-2 h-2 rounded-full bg-indigo-500 flex-shrink-0" />
        <span className="font-semibold text-sm text-indigo-800 flex-1">elements</span>
        <span className="text-xs text-indigo-500 font-mono bg-white border border-indigo-200 px-2 py-0.5 rounded">
          {elementEntries.length} elementos
        </span>
        <button
          onClick={e => { e.stopPropagation(); setAllExpanded(x => !x) }}
          className="flex items-center gap-1 text-xs text-indigo-500 hover:text-indigo-700 bg-white border border-indigo-200 hover:border-indigo-400 px-2 py-0.5 rounded transition-colors"
        >
          {allExpanded ? <MinusIcon className="w-3 h-3" /> : <PlusIcon className="w-3 h-3" />}
          {allExpanded ? 'colapsar' : 'expandir'} todo
        </button>
        <span className={`flex items-center justify-center w-5 h-5 rounded transition-colors ${open ? 'bg-indigo-200 text-indigo-700' : 'bg-indigo-100 text-indigo-500'}`}>
          {open ? <ChevronDownIcon className="w-3 h-3" /> : <ChevronRightIcon className="w-3 h-3" />}
        </span>
      </button>

      {open && (
        <div className="p-3 space-y-2 border-t border-indigo-100 bg-white">
          {metaEntries.map(([k, v]) => (
            <div key={k} className="flex items-center gap-2 px-2 py-1">
              <span className="font-mono text-xs text-violet-700 font-medium">{k}:</span>
              <EditableValue value={v} path={[...path, k]} onUpdate={onUpdate} />
            </div>
          ))}
          {elementEntries.map(([k, v]) => (
            <ElementCard key={`${k}-${allExpanded}`} keyName={k} value={v} path={[...path, k]} onUpdate={onUpdate} forceOpen={allExpanded} />
          ))}
          {elements.expectedFlow && (
            <ExpectedFlowSection expectedFlow={elements.expectedFlow} path={[...path, 'expectedFlow']} onUpdate={onUpdate} />
          )}
          {elements.formFlow && (
            <FormFlowSection formFlow={elements.formFlow} path={[...path, 'formFlow']} onUpdate={onUpdate} />
          )}
        </div>
      )}
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────
const TemplateJsonViewer = ({ template, campaignName, onSave }) => {
  const [copied, setCopied] = useState(false)
  const [rawMode, setRawMode] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [saveOk, setSaveOk] = useState(false)
  const [saveError, setSaveError] = useState(null)

  // Parsed base data (read-only reference)
  const parsedData = useMemo(() => {
    if (!template) return null
    try {
      const raw = typeof template === 'string' ? JSON.parse(template) : template
      // Deep-parse any nested JSON strings
      const deepParse = (v) => {
        if (typeof v === 'string') { try { return deepParse(JSON.parse(v)) } catch { return v } }
        if (Array.isArray(v)) return v.map(deepParse)
        if (v !== null && typeof v === 'object') {
          const r = {}
          for (const k in v) r[k] = deepParse(v[k])
          return r
        }
        return v
      }
      return deepParse(raw)
    } catch { return null }
  }, [template])

  // Mutable copy for editing
  const [editedData, setEditedData] = useState(null)
  useEffect(() => {
    if (parsedData) setEditedData(deepClone(parsedData))
  }, [parsedData])

  // JSON textarea state
  const [jsonText, setJsonText] = useState('')
  const [jsonError, setJsonError] = useState(null)
  const skipTextSync = useRef(false)

  // Sync editedData → jsonText
  useEffect(() => {
    if (skipTextSync.current) { skipTextSync.current = false; return }
    if (editedData) setJsonText(JSON.stringify(editedData, null, 2))
  }, [editedData])

  const isDirty = useMemo(() => {
    if (!parsedData || !editedData) return false
    return JSON.stringify(parsedData) !== JSON.stringify(editedData)
  }, [parsedData, editedData])

  // Update a value at a given path in editedData
  const handleUpdate = useCallback((path, value) => {
    setEditedData(prev => setAtPath(prev, path, value))
    setJsonError(null)
  }, [])

  // Handle raw textarea changes
  const handleJsonTextChange = (e) => {
    const text = e.target.value
    setJsonText(text)
    try {
      const parsed = JSON.parse(text)
      skipTextSync.current = true
      setEditedData(parsed)
      setJsonError(null)
    } catch (err) {
      setJsonError(err.message)
    }
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(jsonText)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) { console.error(err) }
  }

  const handleSave = async () => {
    if (!onSave || !editedData) return
    setIsSaving(true)
    setSaveError(null)
    try {
      await onSave(editedData)
      setSaveOk(true)
      setTimeout(() => setSaveOk(false), 3000)
    } catch (err) {
      console.error('Save error:', err)
      setSaveError(err?.message || 'Error al guardar')
      setTimeout(() => setSaveError(null), 5000)
    } finally {
      setIsSaving(false)
    }
  }

  const handleReset = () => {
    if (parsedData) {
      setEditedData(deepClone(parsedData))
      setJsonError(null)
    }
  }

  const stats = useMemo(() => {
    if (!editedData) return {}
    const els = editedData.elements || {}
    const keys = Object.keys(els)
    const elementKeys = keys.filter(k => !['type', 'expectedFlow', 'formFlow', 'rbmFlowAliasesMap'].includes(k))
    const ef = els.expectedFlow || editedData.expectedFlow
    const ff = editedData.formFlow || els.formFlow
    return {
      elements: elementKeys.length,
      validations: ef ? Object.keys(ef).length : 0,
      formFlow: ff ? ff.length : 0,
      rbmType: editedData.rbmType,
      sender: editedData.sender,
    }
  }, [editedData])

  if (!parsedData || !editedData) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 rounded-xl border border-dashed border-gray-300">
        <div className="text-center">
          <CodeBracketIcon className="h-10 w-10 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-400 text-sm">No hay datos de template para mostrar</p>
        </div>
      </div>
    )
  }

  const { elements, ...metaFields } = editedData
  // All primitive fields go to metadata card
  const topMeta = Object.entries(metaFields).filter(([, v]) =>
    v === null || typeof v !== 'object'
  )
  // Objects and arrays get their own section cards
  const topSections = Object.entries(metaFields).filter(([, v]) =>
    v !== null && typeof v === 'object'
  )

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">
      {/* Header */}
      <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between gap-3 flex-shrink-0">
        <div className="flex items-center gap-2">
          <CodeBracketIcon className="h-5 w-5 text-indigo-500" />
          <h3 className="text-sm font-semibold text-gray-800">JSON del Template</h3>
          {campaignName && (
            <span className="text-xs text-gray-400 font-mono bg-gray-100 px-2 py-0.5 rounded">{campaignName}</span>
          )}
          {isDirty && (
            <span className="text-xs bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full font-medium animate-pulse">
              cambios sin guardar
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isDirty && (
            <button
              onClick={handleReset}
              title="Descartar cambios"
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-500 hover:bg-gray-50 transition-all"
            >
              <ArrowPathIcon className="h-3.5 w-3.5" />
              Resetear
            </button>
          )}
          <button
            onClick={() => setRawMode(r => !r)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
              rawMode ? 'bg-gray-800 text-white border-gray-800' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            <CodeBracketIcon className="h-3.5 w-3.5" />
            {rawMode ? 'Árbol' : 'Raw'}
          </button>
          <button
            onClick={handleCopy}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
              copied ? 'bg-green-100 text-green-700 border-green-200' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {copied
              ? <><CheckIcon className="h-3.5 w-3.5" />Copiado</>
              : <><DocumentDuplicateIcon className="h-3.5 w-3.5" />Copiar</>}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="px-5 py-2 bg-gray-50/50 border-b border-gray-100 flex items-center gap-2 text-xs flex-wrap flex-shrink-0">
        {stats.elements > 0 && (
          <span className="flex items-center gap-1 bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full font-medium">
            <EyeIcon className="h-3 w-3" />{stats.elements} elementos
          </span>
        )}
        {stats.validations > 0 && <span className="bg-purple-50 text-purple-600 px-2 py-0.5 rounded-full font-medium">{stats.validations} validaciones</span>}
        {stats.formFlow > 0 && <span className="bg-green-50 text-green-600 px-2 py-0.5 rounded-full font-medium">{stats.formFlow} formFlow</span>}
        {stats.rbmType && <span className="bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full font-medium">{stats.rbmType}</span>}
        {stats.sender && <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">sender: {stats.sender}</span>}
        <span className="ml-auto font-mono text-gray-400">{jsonText.length.toLocaleString()} chars</span>
      </div>

      {/* Tree or raw */}
      <div className="overflow-auto" style={{ maxHeight: '52vh' }}>
        {rawMode ? (
          <pre className="p-5 text-xs font-mono leading-relaxed text-gray-800">{jsonText}</pre>
        ) : (
          <div className="p-4 space-y-3">
            {/* metadata */}
            {topMeta.length > 0 && (
              <div className="rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-gray-400" />
                  <span className="font-semibold text-sm text-gray-700">metadata</span>
                </div>
                <div className="p-3 space-y-1">
                  {topMeta.map(([k, v]) => (
                    <div key={k} className="flex items-start gap-2 px-2 py-1 rounded hover:bg-gray-50">
                      <span className="font-mono text-xs text-violet-700 font-medium flex-shrink-0">{k}:</span>
                      <EditableValue value={v} path={[k]} onUpdate={handleUpdate} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* elements */}
            {elements && typeof elements === 'object' && (
              <ElementsSection elements={elements} path={['elements']} onUpdate={handleUpdate} />
            )}

            {/* other top-level sections with custom renderers for known types */}
            {topSections.map(([k, v]) => {
              if (k === 'formFlow' && Array.isArray(v))
                return <FormFlowSection key={k} formFlow={v} path={[k]} onUpdate={handleUpdate} />
              if (k === 'expectedFlow' && v && typeof v === 'object')
                return <ExpectedFlowSection key={k} expectedFlow={v} path={[k]} onUpdate={handleUpdate} />
              return <JsonNode key={k} keyName={k} value={v} path={[k]} onUpdate={handleUpdate} depth={0} defaultOpen={false} />
            })}
          </div>
        )}
      </div>

      {/* Bottom: JSON textarea + Save */}
      <div className="border-t border-gray-200 flex-shrink-0">
        <div className="px-4 pt-3 pb-1 flex items-center justify-between">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">JSON sincronizado</span>
          {jsonError && (
            <span className="text-xs text-red-500 font-mono bg-red-50 px-2 py-0.5 rounded">⚠ {jsonError}</span>
          )}
        </div>
        <div className="px-4 pb-3">
          <textarea
            value={jsonText}
            onChange={handleJsonTextChange}
            spellCheck={false}
            rows={8}
            className={`w-full text-xs font-mono leading-relaxed border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 resize-y bg-gray-50 transition-colors ${
              jsonError
                ? 'border-red-300 focus:ring-red-200 bg-red-50'
                : isDirty
                ? 'border-amber-300 focus:ring-amber-200 bg-amber-50/30'
                : 'border-gray-200 focus:ring-indigo-200'
            }`}
          />
        </div>

        {/* Save bar */}
        {onSave && (
          <div className={`px-4 py-3 border-t flex items-center justify-between gap-3 transition-colors ${
            saveError ? 'bg-red-50 border-red-200' : isDirty ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-100'
          }`}>
            <span className={`text-xs ${saveError ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
              {saveError
                ? `✗ ${saveError}`
                : isDirty
                  ? 'Hay cambios pendientes — edita en el árbol o en el JSON directamente'
                  : saveOk ? '✓ Template guardada correctamente' : 'Sin cambios'}
            </span>
            <button
              onClick={handleSave}
              disabled={!isDirty || isSaving || !!jsonError}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all shadow-sm flex-shrink-0 ${
                isDirty && !jsonError
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700 active:scale-95'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {isSaving ? (
                <><ArrowPathIcon className="h-4 w-4 animate-spin" />Guardando…</>
              ) : saveOk ? (
                <><CheckIcon className="h-4 w-4" />Guardado</>
              ) : (
                'Guardar template'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default TemplateJsonViewer
